import { BaseCanvasPack } from "./BaseCanvasPack";

export default class RetroArcadeCanvasPack extends BaseCanvasPack {
  drawBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number,
    level?: number
  ): void {
    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#2a0a3b");
    gradient.addColorStop(1, "#000000");
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
  }

  drawFrame(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {

    // 8-bit border (dashed/checkered)
    const borderWidth = 4;
    context.lineWidth = borderWidth;
    
    const segmentSize = 10;
    let colorToggle = false;
    
    // Top
    for (let x = 0; x < width; x += segmentSize) {
      context.strokeStyle = colorToggle ? "#FF00FF" : "#00FFFF";
      context.strokeRect(x, 0, segmentSize, borderWidth);
      colorToggle = !colorToggle;
    }
    // Bottom
    for (let x = 0; x < width; x += segmentSize) {
      context.strokeStyle = colorToggle ? "#FF00FF" : "#00FFFF";
      context.strokeRect(x, height - borderWidth, segmentSize, borderWidth);
      colorToggle = !colorToggle;
    }
    // Left
    for (let y = 0; y < height; y += segmentSize) {
      context.strokeStyle = colorToggle ? "#FF00FF" : "#00FFFF";
      context.strokeRect(0, y, borderWidth, segmentSize);
      colorToggle = !colorToggle;
    }
    // Right
    for (let y = 0; y < height; y += segmentSize) {
      context.strokeStyle = colorToggle ? "#FF00FF" : "#00FFFF";
      context.strokeRect(width - borderWidth, y, borderWidth, segmentSize);
      colorToggle = !colorToggle;
    }
  }

  drawOverlay(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    if (frameTime !== undefined) {
      this.drawScanlines(context, width, height, frameTime);
      this.drawCRTCurvature(context, width, height);
      this.drawScreenFlicker(context, width, height, frameTime);
    }
  }

  private drawScanlines(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    
    // Static scanlines (dark horizontal lines)
    context.fillStyle = "rgba(0, 0, 0, 0.3)";
    for (let y = 0; y < height; y += 3) {
      context.fillRect(0, y, width, 1);
    }
    
    // Moving scanline bar (brighter band moving down)
    const barY = (frameTime * 0.15) % (height + 200) - 100;
    const gradient = context.createLinearGradient(0, barY, 0, barY + 50);
    gradient.addColorStop(0, "rgba(255, 255, 255, 0)");
    gradient.addColorStop(0.5, "rgba(255, 255, 255, 0.05)");
    gradient.addColorStop(1, "rgba(255, 255, 255, 0)");
    
    context.fillStyle = gradient;
    context.fillRect(0, barY, width, 50);
    
    context.restore();
  }

  private drawCRTCurvature(
    context: CanvasRenderingContext2D,
    width: number,
    height: number
  ): void {
    context.save();
    
    // Vignette effect (darker corners)
    const gradient = context.createRadialGradient(width/2, height/2, height * 0.4, width/2, height/2, height * 0.8);
    gradient.addColorStop(0, "rgba(0,0,0,0)");
    gradient.addColorStop(0.8, "rgba(0,0,0,0.4)");
    gradient.addColorStop(1, "rgba(0,0,0,0.8)");
    
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
    
    // RGB Shift / Chromatic Aberration on edges (simulated)
    // We can't easily shift the whole canvas content here without a temp canvas,
    // but we can draw colored lines at the edges to fake it.
    context.globalCompositeOperation = "screen";
    context.strokeStyle = "rgba(255, 0, 0, 0.2)";
    context.lineWidth = 2;
    context.strokeRect(2, 2, width - 4, height - 4);
    
    context.strokeStyle = "rgba(0, 0, 255, 0.2)";
    context.strokeRect(-2, -2, width + 4, height + 4);
    
    context.restore();
  }

  private drawScreenFlicker(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    
    // Subtle random flicker
    // Use a sine wave combined with random noise for a "bad connection" feel
    const flicker = Math.sin(frameTime * 0.05) * 0.02 + (Math.random() * 0.03);
    
    if (flicker > 0) {
      context.fillStyle = `rgba(255, 255, 255, ${flicker})`;
      context.globalCompositeOperation = "overlay";
      context.fillRect(0, 0, width, height);
    }
    
    context.restore();
  }

  getBorderStyle(): string {
    return "4px solid #FF00FF";
  }

  getBoxShadow(frameTime?: number): string {
    return "0 0 15px #FF00FF, inset 0 0 20px rgba(0,0,0,0.5)";
  }

  getBackdropFilter(): string {
    return "none";
  }
}
